/**
 * Timeline state (brief §9).
 *
 * `cursor` is the current instant; plume opacity on the map is derived from how
 * old each detection is relative to it. `windowDays` is the accumulation window
 * ("Разрыв"): detections older than this drop out entirely, and within it they
 * fade with age. Opacity therefore encodes *time*, not emission strength.
 *
 * The range [min, max] follows the applied filter period (or the dataset bounds
 * when no period is set); setRange re-clamps the cursor into it.
 */
import { create } from "zustand";

export const DAY_MS = 86_400_000;
export const WINDOW_OPTIONS = [7, 30, 60, 90] as const;
export const SPEED_OPTIONS = [1, 2, 4] as const;

interface TimelineState {
  min: number;
  max: number;
  cursor: number;
  windowDays: number;
  isPlaying: boolean;
  speed: number;
  /** False until the first real range arrives, so cursor can default to max. */
  rangeSet: boolean;

  setRange: (min: number, max: number) => void;
  setCursor: (ms: number) => void;
  setWindowDays: (days: number) => void;
  play: () => void;
  pause: () => void;
  togglePlay: () => void;
  setSpeed: (speed: number) => void;
}

export const useTimelineStore = create<TimelineState>((set, get) => ({
  min: 0,
  max: 0,
  cursor: 0,
  windowDays: 30,
  isPlaying: false,
  speed: 1,
  rangeSet: false,

  setRange: (min, max) => {
    const s = get();
    const cursor = !s.rangeSet ? max : Math.min(max, Math.max(min, s.cursor));
    set({ min, max, cursor, rangeSet: true });
  },

  setCursor: (ms) => {
    const { min, max } = get();
    set({ cursor: Math.min(max, Math.max(min, ms)) });
  },

  setWindowDays: (days) => set({ windowDays: days }),

  play: () => {
    const { cursor, max, min } = get();
    // Restart from the beginning if parked at the end.
    set({ isPlaying: true, cursor: cursor >= max ? min : cursor });
  },
  pause: () => set({ isPlaying: false }),
  togglePlay: () => (get().isPlaying ? get().pause() : get().play()),
  setSpeed: (speed) => set({ speed }),
}));
